"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Icon } from "@iconify/react";
import useAuthInfo from "@/store/auth";
import { AjaxGet, AjaxPost, AjaxPostAnonFile } from "@/util/Ajax";
import Register from "@/app/(auth)/register/page";
import Login from "@/app/(auth)/login/page";

interface IndustryOption {
  encId: string;
  name: string;
}

interface DiscoverSocial {
  platform: string;
  url: string;
}

interface SubscriptionTier {
  encTypeId: string;
  name: string;
  cost: number;
  trialDurationDays?: number | null;
  paidAccessMonths?: number | null;
  contentLimit: number;
  allowSocialContent: boolean;
  allowEmail: boolean;
  allowPhone: boolean;
  allowWhatsapp: boolean;
  allowWebsite: boolean;
}

interface SubscriptionStatus {
  hasActiveSubscription: boolean;
  contentLimit: number;
  allowSocialContent: boolean;
  allowEmail: boolean;
  allowPhone: boolean;
  allowWhatsapp: boolean;
  allowWebsite: boolean;
}

interface ListingPayload {
  encryptedId?: string;
  title: string;
  description?: string;
  imageUrl?: string;
  location?: string;
  isRemote?: boolean;
  website?: string;
  email?: string;
  phone?: string;
  whatsapp?: string;
  industry?: string;
  socials: DiscoverSocial[];
  gallery: string[];
}

const ALL_ALLOWED = {
  social: true,
  email: true,
  phone: true,
  whatsapp: true,
  website: true,
};

const NONE_ALLOWED = {
  social: false,
  email: false,
  phone: false,
  whatsapp: false,
  website: false,
};

const WA_PREFIX = "https://wa.me/+27";

// "072 123 4567" | "+27721234567" | "27721234567" | "https://wa.me/+27721234567"
// => "721234567" (9 digits, no leading zero, no country code)
const toLocalWhatsapp = (value?: string) => {
  const digits = (value || "").replace(/\D/g, "");
  if (!digits) return "";

  let local = digits;
  if (local.startsWith("27")) local = local.slice(2);
  else if (local.startsWith("0")) local = local.slice(1);

  return local;
};

// => "https://wa.me/+27721234567", or "" if we can't make a valid SA number
const toWhatsappUrl = (value?: string) => {
  const local = toLocalWhatsapp(value);
  if (local.length !== 9) return "";
  return `${WA_PREFIX}${local}`;
};

export default function DiscoverCreatePage() {
  const router = useRouter();
  const userData = useAuthInfo((state) => state);
  const formRef = useRef<HTMLFormElement>(null);

  const [authHydrated, setAuthHydrated] = useState(false);
  const [authMode, setAuthMode] = useState<"register" | "login">("register");
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [startingSubscription, setStartingSubscription] = useState(false);

  // Subscription tiers
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [selectedTypeEncId, setSelectedTypeEncId] = useState("");
  const [canClaimTrial, setCanClaimTrial] = useState(false);
  const [subStatus, setSubStatus] = useState<SubscriptionStatus | null>(null);

  // Set from ?plan=trial — preselects the free tier and jumps past the plan step.
  const [wantsTrial, setWantsTrial] = useState(false);
  const [trialApplied, setTrialApplied] = useState(false);

  const [industries, setIndustries] = useState<IndustryOption[]>([]);
  const [editingEncId, setEditingEncId] = useState("");
  const [loadedIndustryName, setLoadedIndustryName] = useState("");

  const [encIndustryId, setEncIndustryId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [location, setLocation] = useState("");
  const [isRemote, setIsRemote] = useState(false);
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  // Local part only (9 digits) — the +27 / wa.me wrapper is added on save.
  const [whatsapp, setWhatsapp] = useState("");
  const [socials, setSocials] = useState<DiscoverSocial[]>([{ platform: "", url: "" }]);
  const [gallery, setGallery] = useState<string[]>([]);

  const hasActiveSubscription = !!subStatus?.hasActiveSubscription;

  // If they already have an active subscription (e.g. editing), skip plan + payment.
  const skipPlan = hasActiveSubscription || !!editingEncId;
  const firstStep = skipPlan ? 2 : 1;
  const maxStep = 5;

  const selectedTier = useMemo(
    () => tiers.find((t) => t.encTypeId === selectedTypeEncId),
    [tiers, selectedTypeEncId]
  );

  const isTrialTier = (t: SubscriptionTier) => t.cost <= 0;

  // What the current plan permits. An active subscription is the source of truth;
  // otherwise fall back to the tier being picked in the wizard.
  const perms = useMemo(() => {
    if (hasActiveSubscription && subStatus) {
      return {
        social: subStatus.allowSocialContent,
        email: subStatus.allowEmail,
        phone: subStatus.allowPhone,
        whatsapp: subStatus.allowWhatsapp,
        website: subStatus.allowWebsite,
      };
    }
    if (selectedTier) {
      return {
        social: selectedTier.allowSocialContent,
        email: selectedTier.allowEmail,
        phone: selectedTier.allowPhone,
        whatsapp: selectedTier.allowWhatsapp,
        website: selectedTier.allowWebsite,
      };
    }
    return skipPlan ? ALL_ALLOWED : NONE_ALLOWED;
  }, [hasActiveSubscription, subStatus, selectedTier, skipPlan]);

  // Enforcement limits from the selected tier. When skipping the plan step
  // (active subscriber / editing), we don't cap client-side here.
  const contentLimit = skipPlan ? Infinity : (selectedTier?.contentLimit ?? 0);
  const allowSocial = perms.social;
  const galleryFull = gallery.length >= contentLimit;
  const hasAnyContactField = perms.website || perms.email || perms.phone || perms.whatsapp;
  const whatsappInvalid = perms.whatsapp && whatsapp.length > 0 && whatsapp.length !== 9;

  useEffect(() => {
    let isMounted = true;

    const hydrateAuth = async () => {
      await useAuthInfo.persist.rehydrate();
      if (isMounted) setAuthHydrated(true);
    };

    hydrateAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  // ?plan=trial — read once on mount.
  useEffect(() => {
    const plan = new URLSearchParams(window.location.search).get("plan");
    if (plan?.toLowerCase() === "trial") setWantsTrial(true);
  }, []);

  useEffect(() => {
    if (!userData.apiToken) return;
    loadIndustries();
    loadMyListing();
    loadTiers();
    loadTrialEligibility();
    loadSubscriptionStatus();
  }, [userData.apiToken]);

  // Make sure we start on the correct first step once we know whether to skip the plan.
  useEffect(() => {
    setStep((s) => (s < firstStep ? firstStep : s));
  }, [firstStep]);

  // Came in from the "free month" banner: pick the trial tier and drop them
  // straight onto Basics. Only fires once, and only if they're actually eligible.
  useEffect(() => {
    if (!wantsTrial || trialApplied || skipPlan) return;
    if (!tiers.length || !canClaimTrial) return;

    const trial = tiers.find(isTrialTier);
    if (!trial) return;

    setSelectedTypeEncId(trial.encTypeId);
    setStep((s) => (s < 2 ? 2 : s));
    setTrialApplied(true);
  }, [wantsTrial, trialApplied, skipPlan, tiers, canClaimTrial]);

  const currentIndustryName = useMemo(
    () => industries.find((x) => x.encId === encIndustryId)?.name || "",
    [industries, encIndustryId]
  );

  const loadIndustries = async () => {
    const res = await AjaxGet(userData, "Discover/Industries");
    if (res?.data) {
      setIndustries(res.data || []);
    }
  };

  const loadTiers = async () => {
    const res = await AjaxGet(userData, "Discover/SubscriptionTypes");
    if (res?.data) {
      setTiers(res.data || []);
    }
  };

  const loadTrialEligibility = async () => {
    const res = await AjaxGet(userData, "Discover/CanClaimTrial");
    setCanClaimTrial(res?.data === true);
  };

  const loadSubscriptionStatus = async () => {
    const res = await AjaxGet(userData, "Discover/MySubscription");
    setSubStatus(res?.data ?? null);
  };

  const loadMyListing = async () => {
    const res = await AjaxGet(userData, "Discover/MyListing");
    const data: ListingPayload | undefined = res?.data;
    if (!data?.encryptedId) return;

    setEditingEncId(data.encryptedId);
    setTitle(data.title || "");
    setDescription(data.description || "");
    setImageUrl(data.imageUrl || "");
    setLocation(data.location || "");
    setIsRemote(Boolean(data.isRemote));
    setWebsite(data.website || "");
    setEmail(data.email || "");
    setPhone(data.phone || "");
    setWhatsapp(toLocalWhatsapp(data.whatsapp));
    setLoadedIndustryName(data.industry || "");
    setSocials(data.socials?.length ? data.socials : [{ platform: "", url: "" }]);
    setGallery(data.gallery || []);
  };

  useEffect(() => {
    if (!loadedIndustryName || industries.length === 0) return;
    const match = industries.find((x) => x.name === loadedIndustryName);
    if (match) setEncIndustryId(match.encId);
  }, [loadedIndustryName, industries]);

  const parseUploadResponse = (raw: unknown) => {
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return typeof parsed === "string" ? parsed : raw;
      } catch {
        return raw.replace(/^"|"$/g, "");
      }
    }
    return "";
  };

  const uploadSingleImage = async (file: File, isMainImage: boolean) => {
    const form = new FormData();
    form.append("file", file);

    if (isMainImage) setUploadingMain(true);
    else setUploadingGallery(true);

    try {
      const res = await AjaxPostAnonFile("Upload/Document", form);
      const uploadedUrl = parseUploadResponse(res?.data);
      if (!uploadedUrl) return;

      if (isMainImage) setImageUrl(uploadedUrl);
      else setGallery((prev) => [...prev, uploadedUrl]);
    } finally {
      if (isMainImage) setUploadingMain(false);
      else setUploadingGallery(false);
    }
  };

  const onMainImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadSingleImage(file, true);
    e.target.value = "";
  };

  const onGallerySelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      // Hard cap: stop once the tier's content limit is reached
      if (gallery.length >= contentLimit) {
        break;
      }
      // eslint-disable-next-line no-await-in-loop
      await uploadSingleImage(file, false);
    }
    e.target.value = "";
  };

  const addSocialRow = () => setSocials((prev) => [...prev, { platform: "", url: "" }]);

  const updateSocial = (index: number, key: keyof DiscoverSocial, value: string) => {
    setSocials((prev) => prev.map((s, i) => (i === index ? { ...s, [key]: value } : s)));
  };

  const removeSocial = (index: number) => {
    setSocials((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [{ platform: "", url: "" }];
    });
  };

  const moveGallery = (index: number, direction: -1 | 1) => {
    setGallery((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const clone = [...prev];
      [clone[index], clone[target]] = [clone[target], clone[index]];
      return clone;
    });
  };

  const removeGallery = (index: number) => {
    setGallery((prev) => prev.filter((_, i) => i !== index));
  };

  // Can't leave the plan step without picking a tier (unless plan step is skipped)
  const canAdvance = () => {
    if (step === 1 && !skipPlan && !selectedTypeEncId) return false;
    if (step === 2 && whatsappInvalid) return false;
    return true;
  };

  const nextStep = () => {
    if (!canAdvance()) return;
    setStep((s) => Math.min(maxStep, s + 1));
  };
  const prevStep = () => setStep((s) => Math.max(firstStep, s - 1));

  const saveListing = async () => {
    setSaving(true);
    try {
      const filteredSocials = allowSocial
        ? socials
            .map((s) => ({ platform: s.platform.trim(), url: s.url.trim() }))
            .filter((s) => s.platform && s.url)
        : [];

      // Enforce content limit on save as a final guard
      const cappedGallery =
        contentLimit === Infinity ? gallery : gallery.slice(0, contentLimit);

      const payload = {
        encIndustryId,
        title: title.trim(),
        description: description.trim(),
        imageUrl,
        location: location.trim(),
        isRemote,
        website: perms.website ? website.trim() : "",
        email: perms.email ? email.trim() : "",
        phone: perms.phone ? phone.trim() : "",
        whatsapp: perms.whatsapp ? toWhatsappUrl(whatsapp) : "",
        socials: filteredSocials,
        gallery: cappedGallery,
      } as any;

      const res = editingEncId
        ? await AjaxPost(userData, "Discover/Update", { ...payload, encListingId: editingEncId })
        : await AjaxPost(userData, "Discover/Create", payload);

      if (!res?.data?.encryptedId) {
        return;
      }

      const createdEncId = res.data.encryptedId as string;
      const createdSlug = res.data.slug || createdEncId;

      // If they already have an active subscription (or are editing), no plan/payment needed.
      if (skipPlan) {
        router.push(`/discover/${createdSlug}`);
        return;
      }

      // Branch on the selected tier: Trial => claim (free), paid => PayGate.
      if (selectedTier && isTrialTier(selectedTier)) {
        const claimRes = await AjaxPost(userData, "Discover/ClaimTrial", {});
        if (claimRes?.data) {
          router.push(`/discover/${createdSlug}`);
          return;
        }
        alert(
          claimRes?.response?.data?.message ??
            "Could not activate the free trial. Please try again."
        );
        return;
      }

      // Paid tier => start PayGate with the selected type
      setStartingSubscription(true);
      const payRes = await AjaxPost(userData, "Discover/Subscribe", {
        returnToListingId: createdEncId,
        encTypeId: selectedTypeEncId,
      });
      const payRequestId = payRes?.data?.paY_REQUEST_ID;
      const checksum = payRes?.data?.checksum;
      if (payRequestId && checksum && formRef.current) {
        (formRef.current.elements.namedItem("PAY_REQUEST_ID") as HTMLInputElement).value = payRequestId;
        (formRef.current.elements.namedItem("CHECKSUM") as HTMLInputElement).value = checksum;
        formRef.current.submit();
        return;
      }

      // Fallback if payment couldn't start
      router.push(`/discover/${createdSlug}`);
    } finally {
      setSaving(false);
      setStartingSubscription(false);
    }
  };

  const formatCurrency = (value: number) => `R ${Number(value || 0).toFixed(2)}`;

  if (!authHydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Icon icon="heroicons:arrow-path" className="h-8 w-8 animate-spin text-cyan-600" />
        <span className="sr-only">Checking your account...</span>
      </div>
    );
  }

  if (!userData.apiToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50 px-4 py-12 dark:from-slate-950 dark:via-slate-900 dark:to-cyan-950/30">
        <div className="container mx-auto mb-8 max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-600">Discover Wizard</p>
          <h1 className="mt-3 text-3xl font-bold text-slate-900 dark:text-white">
            Create your free business listing
          </h1>
          <p className="mt-3 text-slate-600 dark:text-slate-300">
            Create an account or sign in to continue building your listing.
          </p>
        </div>

        {authMode === "register" ? (
          <Register
            embedded
            onSuccess={() => undefined}
            onShowLogin={() => setAuthMode("login")}
          />
        ) : (
          <Login
            embedded
            onSuccess={() => undefined}
            onShowRegister={() => setAuthMode("register")}
          />
        )}
      </div>
    );
  }

  const stepLabels = ["Plan", "Basics", "Brand", "Social & Gallery", "Review"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 py-8 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Hidden PayGate form — submitted programmatically after Subscribe returns PAY_REQUEST_ID + CHECKSUM */}
      <form
        ref={formRef}
        action="https://secure.paygate.co.za/payweb3/process.trans"
        method="POST"
        target="_self"
        style={{ display: "none" }}
      >
        <input type="hidden" name="PAY_REQUEST_ID" />
        <input type="hidden" name="CHECKSUM" />
      </form>

      <div className="container mx-auto max-w-5xl px-4">
        <div className="mb-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-600">Discover Wizard</p>
          <h1 className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">
            {editingEncId ? "Edit Your Discovery Listing" : "Create Your Discovery Listing"}
          </h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Build your profile in guided steps, upload media, arrange gallery order, and publish.
          </p>

          {trialApplied && (
            <p className="mt-3 rounded-xl bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 dark:bg-emerald-950/30">
              Free tier selected — it activates when you submit your listing.
            </p>
          )}

          <div className="mt-5 grid grid-cols-5 gap-3">
            {stepLabels.map((label, idx) => {
              const stepNo = idx + 1;
              const active = step === stepNo;
              const done = step > stepNo;
              const hidden = stepNo === 1 && skipPlan;
              return (
                <div
                  key={label}
                  className={`rounded-xl border px-3 py-2 text-xs font-semibold ${
                    hidden
                      ? "border-slate-100 bg-slate-50 text-slate-300"
                      : active
                        ? "border-cyan-500 bg-cyan-50 text-cyan-700"
                        : done
                          ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                          : "border-slate-200 bg-slate-50 text-slate-500"
                  }`}
                >
                  {stepNo}. {label}
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          {step === 1 && !skipPlan && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Choose Your Plan</h2>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Your plan determines how many gallery items you can add, which contact details you can publish, and whether social links are included.
              </p>

              <div className="grid gap-4 md:grid-cols-3">
                {tiers.map((tier) => {
                  const trial = isTrialTier(tier);
                  const trialLocked = trial && !canClaimTrial;
                  const selected = selectedTypeEncId === tier.encTypeId;
                  const contactBits = [
                    tier.allowEmail && "Email",
                    tier.allowPhone && "Phone",
                    tier.allowWhatsapp && "WhatsApp",
                    tier.allowWebsite && "Website",
                  ].filter(Boolean) as string[];
                  return (
                    <button
                      key={tier.encTypeId}
                      type="button"
                      disabled={trialLocked}
                      onClick={() => setSelectedTypeEncId(tier.encTypeId)}
                      className={`rounded-2xl border p-5 text-left transition ${
                        selected
                          ? "border-cyan-500 bg-cyan-50 ring-2 ring-cyan-200 dark:bg-cyan-950/30"
                          : trialLocked
                            ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-60"
                            : "border-slate-200 bg-white hover:border-cyan-300 dark:border-slate-700 dark:bg-slate-900"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-base font-bold text-slate-900 dark:text-white">
                          {tier.name}
                        </span>
                        {selected && <Icon icon="heroicons:check-circle" className="text-xl text-cyan-600" />}
                      </div>
                      <p className="mt-2 text-2xl font-black text-cyan-600">
                        {trial ? "Free" : formatCurrency(tier.cost)}
                      </p>
                      <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-300">
                        {trial && tier.trialDurationDays && <li>Free offer: {tier.trialDurationDays} days, subject to eligibility</li>}
                        {!trial && tier.paidAccessMonths && <li>{tier.paidAccessMonths} month{tier.paidAccessMonths === 1 ? "" : "s"} of listing access per purchase</li>}
                        <li>Up to {tier.contentLimit} gallery items</li>
                        <li>{tier.allowSocialContent ? "Social links included" : "No social links"}</li>
                        <li>{contactBits.length ? `Contact: ${contactBits.join(", ")}` : "No contact details"}</li>
                      </ul>
                      {trialLocked && (
                        <p className="mt-3 text-xs font-semibold text-amber-600">
                          Already used
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Basics</h2>
              <select className="w-full rounded-xl border p-3" value={encIndustryId} onChange={(e) => setEncIndustryId(e.target.value)}>
                <option value="">Select industry</option>
                {industries.map((industry) => (
                  <option key={industry.encId} value={industry.encId}>{industry.name}</option>
                ))}
              </select>
              <input className="w-full rounded-xl border p-3" placeholder="Business title" value={title} onChange={(e) => setTitle(e.target.value)} />
              <textarea className="w-full rounded-xl border p-3" rows={5} placeholder="Business description" value={description} onChange={(e) => setDescription(e.target.value)} />
              <div className="grid gap-3 md:grid-cols-2">
                <input className="rounded-xl border p-3" placeholder="Location" value={location} onChange={(e) => setLocation(e.target.value)} />
                <label className="flex items-center gap-3 rounded-xl border p-3"><input type="checkbox" checked={isRemote} onChange={(e) => setIsRemote(e.target.checked)} />This business offers remote services</label>
                {perms.website && (
                  <input className="rounded-xl border p-3" placeholder="Website" value={website} onChange={(e) => setWebsite(e.target.value)} />
                )}
                {perms.email && (
                  <input className="rounded-xl border p-3" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                )}
                {perms.phone && (
                  <input className="rounded-xl border p-3" placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
                )}
                {perms.whatsapp && (
                  <div>
                    <div className={`flex items-center rounded-xl border ${whatsappInvalid ? "border-amber-500" : ""}`}>
                      <span className="pl-3 text-sm font-semibold text-slate-500">+27</span>
                      <input
                        className="w-full bg-transparent p-3 outline-none"
                        placeholder="72 123 4567"
                        inputMode="numeric"
                        value={whatsapp}
                        onChange={(e) => setWhatsapp(toLocalWhatsapp(e.target.value))}
                      />
                    </div>
                    {whatsappInvalid && (
                      <p className="mt-1 text-xs font-semibold text-amber-600">
                        Enter a 9-digit South African number (e.g. 72 123 4567).
                      </p>
                    )}
                  </div>
                )}
              </div>
              {!hasAnyContactField && (
                <p className="text-sm font-semibold text-amber-600">
                  Your plan doesn&apos;t include published contact details.
                </p>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Brand Cover Image</h2>
              <input type="file" accept="image/*" onChange={onMainImageSelect} className="block w-full text-sm" />
              {uploadingMain && <p className="text-sm text-cyan-700">Uploading main image...</p>}
              {imageUrl && (
                <div className="overflow-hidden rounded-2xl border">
                  <img src={imageUrl} alt="Main" className="h-64 w-full object-cover" />
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              {allowSocial && (
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Social Profiles</h2>
                  <div className="mt-3 space-y-2">
                    {socials.map((social, idx) => (
                      <div key={idx} className="grid gap-2 md:grid-cols-[1fr_2fr_auto]">
                        <input className="rounded-xl border p-3" placeholder="Platform" value={social.platform} onChange={(e) => updateSocial(idx, "platform", e.target.value)} />
                        <input className="rounded-xl border p-3" placeholder="URL" value={social.url} onChange={(e) => updateSocial(idx, "url", e.target.value)} />
                        <button type="button" onClick={() => removeSocial(idx)} className="rounded-xl border px-3">Remove</button>
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={addSocialRow} className="mt-3 rounded-xl border px-4 py-2 text-sm">+ Add social</button>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Gallery Upload & Ordering</h2>
                  {contentLimit !== Infinity && (
                    <span className={`text-sm font-semibold ${galleryFull ? "text-amber-600" : "text-slate-500"}`}>
                      {gallery.length} of {contentLimit} used
                    </span>
                  )}
                </div>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={onGallerySelect}
                  disabled={galleryFull}
                  className="mt-3 block w-full text-sm disabled:opacity-50"
                />
                {galleryFull && (
                  <p className="mt-2 text-sm font-semibold text-amber-600">
                    You&apos;ve reached your plan&apos;s gallery limit of {contentLimit}.
                  </p>
                )}
                {uploadingGallery && <p className="mt-2 text-sm text-cyan-700">Uploading gallery image(s)...</p>}

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {gallery.map((url, index) => (
                    <div key={`${url}-${index}`} className="rounded-2xl border p-2">
                      <img src={url} alt={`Gallery ${index + 1}`} className="h-40 w-full rounded-xl object-cover" />
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="text-xs text-slate-500">Order #{index + 1}</span>
                        <div className="flex gap-1">
                          <button type="button" onClick={() => moveGallery(index, -1)} className="rounded border px-2 py-1" disabled={index === 0}><Icon icon="heroicons:arrow-up" /></button>
                          <button type="button" onClick={() => moveGallery(index, 1)} className="rounded border px-2 py-1" disabled={index === gallery.length - 1}><Icon icon="heroicons:arrow-down" /></button>
                          <button type="button" onClick={() => removeGallery(index)} className="rounded border px-2 py-1 text-red-600"><Icon icon="heroicons:trash" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Review & Submit</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border p-4 text-sm">
                  <p><strong>Plan:</strong> {skipPlan ? "Existing subscription" : (selectedTier?.name || "-")}</p>
                  <p><strong>Industry:</strong> {currentIndustryName || "-"}</p>
                  <p><strong>Title:</strong> {title || "-"}</p>
                  <p><strong>Location:</strong> {location || "-"}</p>
                  <p><strong>Remote services:</strong> {isRemote ? "Available" : "Not offered"}</p>
                  {perms.website && <p><strong>Website:</strong> {website || "-"}</p>}
                  {perms.email && <p><strong>Email:</strong> {email || "-"}</p>}
                  {perms.phone && <p><strong>Phone:</strong> {phone || "-"}</p>}
                  {perms.whatsapp && <p><strong>WhatsApp:</strong> {toWhatsappUrl(whatsapp) || "-"}</p>}
                </div>
                <div className="rounded-xl border p-4 text-sm">
                  <p><strong>Main image:</strong> {imageUrl ? "Uploaded" : "Not set"}</p>
                  <p><strong>Gallery images:</strong> {gallery.length}{contentLimit !== Infinity ? ` / ${contentLimit}` : ""}</p>
                  <p><strong>Social links:</strong> {allowSocial ? socials.filter((s) => s.platform && s.url).length : "Not included"}</p>
                  <p><strong>Mode:</strong> {editingEncId ? "Update existing" : "Create new"}</p>
                </div>
              </div>
              {!skipPlan && selectedTier && !isTrialTier(selectedTier) && (
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  On submit you&apos;ll be taken to secure payment for the {selectedTier.name} plan ({formatCurrency(selectedTier.cost)}).
                </p>
              )}
              {!skipPlan && selectedTier && isTrialTier(selectedTier) && (
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Your free trial will be activated on submit — no payment required.
                </p>
              )}
            </div>
          )}

          <div className="mt-8 flex flex-wrap justify-between gap-3">
            <button type="button" onClick={prevStep} disabled={step === firstStep} className="rounded-xl border px-4 py-2 disabled:opacity-40">Back</button>
            {step < maxStep ? (
              <button
                type="button"
                onClick={nextStep}
                disabled={!canAdvance()}
                className="rounded-xl bg-slate-900 px-4 py-2 text-white disabled:opacity-40"
              >
                Continue
              </button>
            ) : (
              <button type="button" onClick={saveListing} disabled={saving || !title || !encIndustryId || whatsappInvalid || (!skipPlan && !selectedTypeEncId)} className="rounded-xl bg-cyan-600 px-4 py-2 text-white disabled:opacity-50">
                {saving
                  ? startingSubscription
                    ? "Starting Subscription Payment..."
                    : "Saving..."
                  : editingEncId
                    ? "Update Listing"
                    : "Create Listing"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
